import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Field, PageHeader, Spinner } from "@/components/Field";
import { FormDialog, useSubmit } from "@/components/dialogs/FormDialog";
import { PlatformUsers } from "@/components/PlatformUsers";

const FEATURES = [["rooms", "Gestione sale"], ["invoices", "Fatture"], ["documents", "Documenti"], ["minutes", "Verbali"], ["stats", "Statistiche eventi"]];

const FeatureToggles = ({ a, onChange }) => (
  <div data-testid={`platform-features-${a.id}`} className="flex flex-wrap gap-x-5 gap-y-2 mt-2">
    {FEATURES.map(([k, l]) => (
      <label key={k} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <Switch data-testid={`platform-feature-${k}-${a.id}`} checked={!!a.features?.[k]} onCheckedChange={(v) => onChange(a, k, v)} /> {l}
      </label>
    ))}
  </div>
);

export default function PlatformPage() {
  const { user, memberships, logout, loadMe } = useAuth();
  const { data, reload } = useFetch(user?.platform_role === "super_admin" ? "/platform/associations" : null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", admin_email: "" });
  const [link, setLink] = useState("");
  const { saving, run } = useSubmit();

  if (user === null) return <Spinner />;
  if (!user || user.platform_role !== "super_admin") return <Navigate to="/" replace />;

  const create = () => run(async () => {
    const { data: r } = await api.post("/platform/associations", f);
    setLink(r.invite_link);
    reload();
  }, "Associazione creata");
  const setStatus = async (a, status) => {
    try { await api.patch(`/platform/associations/${a.id}/status`, { status }); toast.success("Stato aggiornato"); reload(); } catch (e) { toast.error(formatError(e)); }
  };
  const setFeature = async (a, k, v) => {
    try { await api.patch(`/platform/associations/${a.id}/features`, { [k]: v }); toast.success(`${FEATURES.find((x) => x[0] === k)[1]} ${v ? "attivata" : "disattivata"} per ${a.name}`); reload(); loadMe(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-body px-4 lg:px-8 py-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center">
        {memberships.length > 0 ? <Link to="/" data-testid="platform-back" className="inline-flex items-center gap-1 text-sm font-semibold text-primary"><ArrowLeft className="h-4 w-4" /> Torna all'associazione</Link> : <span />}
        <Button data-testid="platform-logout" variant="ghost" onClick={logout}>Esci</Button>
      </div>
      <PageHeader title="Piattaforma" subtitle="Gestione delle associazioni. Da qui non si accede ai contenuti privati delle associazioni.">
        <Button data-testid="platform-new-association" className="h-11" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Nuova associazione</Button>
      </PageHeader>
      <Card><ul className="divide-y">
        {data?.map((a) => (
          <li key={a.id} data-testid={`platform-assoc-${a.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4">
            <div>
              <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{a.name}</span><StatusBadge label={a.status === "active" ? "Attiva" : "Sospesa"} tone={a.status === "active" ? "success" : "warning"} /></div>
              <div className="text-sm text-slate-500">{a.members} membri · {a.products} prodotti · {a.rooms} sale · piano {a.plan} · creata {fmtDateTime(a.created_at)}</div>
              <FeatureToggles a={a} onChange={setFeature} />
            </div>
            <Button data-testid={`platform-toggle-${a.id}`} variant="outline" className="h-11" onClick={() => setStatus(a, a.status === "active" ? "suspended" : "active")}>{a.status === "active" ? "Sospendi" : "Riattiva"}</Button>
          </li>
        ))}
      </ul></Card>
      <PlatformUsers associations={data || []} onChanged={reload} />
      <FormDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setLink(""); setF({ name: "", admin_email: "" }); } }} title="Nuova associazione" onSubmit={create} saving={saving} testId="platform-dialog" submitLabel="Crea">
        <Field label="Nome *"><Input data-testid="platform-name-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11" /></Field>
        <Field label="E-mail dell'amministratore *"><Input data-testid="platform-email-input" type="email" required value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} className="h-11" /></Field>
        {link && <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-2" data-testid="platform-invite-link"><p className="text-sm text-green-900">Link di invito per l'amministratore:</p><div className="flex gap-2"><Input readOnly value={link} className="h-10 text-xs" /><Button type="button" className="h-10" onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Copiato"))}><Copy className="h-4 w-4" /></Button></div></div>}
      </FormDialog>
    </div>
  );
}
